import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import useJobDescriptionState from '@/states/job.state';
import useTenantState from '@/states/tenant.state';
import useCompanyState from '@/states/company.state';
import { useCompanyProfileSteps } from '@/hooks/useCompanyProfileSteps';
import { downloadJobDescriptionPdf } from '@/utils/functions/downloadJobDescriptionPdf';
import type { IJobDescriptionPeran } from '@/types/job-description-peran.types';
import JobDescriptionPeranTable from './JobDescriptionPeranTable';
import DocumentColorPicker, { DEFAULT_DOCUMENT_COLOR } from './DocumentColorPicker';
import type { DataConfig } from '@/utils/hooks/useDefaultDataConfig';
import { useRefetchCompletionStatus } from '@/hooks/useCompletionStatus';
import StepTipsPanel from '@/components/company/StepTipsPanel';

interface JobDescriptionContentProps {
  mode?: 'detail' | 'download';
}

export default function JobDescriptionContent({ mode = 'detail' }: JobDescriptionContentProps) {
  const navigate = useNavigate();
  const { tenant_id } = useParams<{ tenant_id: string }>();
  const { currentTenant } = useTenantState();
  const [showPanel, setShowPanel] = useState(true);
  const [themeColor, setThemeColor] = useState(DEFAULT_DOCUMENT_COLOR);
  const [downloadingId, setDownloadingId] = useState<IJobDescriptionPeran['id'] | null>(null);
  const refetchCompletionStatus = useRefetchCompletionStatus();

  const {
    peranList,
    peranPaginated,
    peranLoading,
    getJobDescriptionPeran,
    jobDescriptionsFull,
    getJobDescriptionsFull,
  } = useJobDescriptionState();

  const [pageConfig, setPageConfig] = useState<DataConfig>({
    page: 0,
    limit: 10,
    order: 'asc',
    orderBy: 'org_level',
  });

  useEffect(() => {
    if (!currentTenant?.id) return;
    getJobDescriptionPeran(currentTenant.id, {
      page: pageConfig.page,
      page_size: pageConfig.limit,
      order: pageConfig.order,
      order_by: pageConfig.orderBy as 'name' | 'job_description_count' | 'org_level',
    });
  }, [
    currentTenant?.id,
    pageConfig.page,
    pageConfig.limit,
    pageConfig.order,
    pageConfig.orderBy,
    getJobDescriptionPeran,
  ]);

  useEffect(() => {
    if (!currentTenant?.id || mode !== 'download') return;
    getJobDescriptionsFull(currentTenant.id);
  }, [currentTenant?.id, mode, getJobDescriptionsFull]);

  const enrichedPeranList = useMemo(() => {
    if (mode !== 'download' || jobDescriptionsFull.length === 0) return peranList;
    return peranList.map(peran => {
      const reportFiles = jobDescriptionsFull
        .filter(jd => jd.org_chart_node_id === peran.id && jd.report_file)
        .flatMap(jd => {
          try {
            const parsed = JSON.parse(jd.report_file);
            return Array.isArray(parsed) ? parsed : [parsed];
          } catch {
            return [];
          }
        })
        .filter((f): f is { url: string; filename: string; original_name: string } => !!f?.url);
      return { ...peran, report_files: reportFiles };
    });
  }, [peranList, jobDescriptionsFull, mode]);

  useCompanyProfileSteps();
  const { companyProfileSteps } = useCompanyState();
  const jobDescStep = companyProfileSteps.find(step =>
    ['role', 'job description'].includes(step.step_name.trim().toLowerCase()),
  );
  const videoLink = jobDescStep?.video_link;
  const tips = jobDescStep?.tips;

  const handleDetail = (row: IJobDescriptionPeran) => {
    navigate(`/${tenant_id}/job-description/peran/${row.id}`);
  };

  const handleDownload = async (row: IJobDescriptionPeran) => {
    if (!currentTenant?.id || downloadingId !== null) return;
    setDownloadingId(row.id);
    try {
      await downloadJobDescriptionPdf({
        tenantId: currentTenant.id,
        nodeId: row.id,
        peranName: row.name,
        division: row.division_key,
        year: new Date().getFullYear(),
        reportFileUrls: row.report_files?.map(f => f.url),
        themeColor,
      });
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <section className="w-full">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 p-5 flex flex-col md:flex-row md:items-center justify-between gap-2 shadow-sm">
        <div className="flex flex-col">
          <h1 className="font-semibold text-lg">
            {mode === 'download' ? 'Download AOS Document' : 'Job Description (Deskripsi Jabatan)'}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-300 mt-1">
            {mode === 'download'
              ? 'Unduh dokumen AOS berdasarkan jabatan yang tersedia.'
              : 'Daftar jabatan fungsional. Klik "Kelola" pada jabatan terkait untuk mendefinisikan rincian tugas (Job Description) masing-masing.'}
          </p>
        </div>

        {mode !== 'download' && (
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setShowPanel(v => !v)}
              className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <ChevronRight size={15} className={`transition-transform duration-200 ${showPanel ? 'rotate-90' : ''}`} />
              {showPanel ? 'Sembunyikan Panduan' : 'Tampilkan Panduan'}
            </button>

            <button
              type="button"
              onClick={async () => {
                await refetchCompletionStatus();
                navigate(`/${tenant_id}/report`);
              }}
              className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg text-white font-medium transition-opacity"
              style={{ background: '#FD6118' }}
            >
              Simpan & Lanjut 🚀
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-col xl:flex-row gap-6 p-5">
        <div className="flex-1 min-w-0">
          {mode === 'download' && <DocumentColorPicker value={themeColor} onChange={setThemeColor} />}

          <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
            <JobDescriptionPeranTable
              isLoading={peranLoading}
              data={{
                content: enrichedPeranList,
                totalElements: peranPaginated?.totalElements ?? 0,
                totalPages: peranPaginated?.totalPages ?? 0,
              }}
              pageConfig={pageConfig}
              setPageConfig={setPageConfig}
              onDetail={handleDetail}
              onDownload={handleDownload}
              downloadingId={downloadingId}
              mode={mode}
            />
          </div>
        </div>

        {mode !== 'download' && showPanel && (
          <div className="w-full xl:w-80 shrink-0">
            <StepTipsPanel tips={tips} videoLink={videoLink} />
          </div>
        )}
      </div>
    </section>
  );
}
